import "./COREMenu.less";
import React, { cloneElement } from "react";
import { Menu, MenuProps } from "antd";
import { TestID, TestIDWrapper } from "../../shared/testids/testids";
import classNames from "classnames";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useHasCoarsePointer } from "../../shared/customHooks/useHasCoarsePointer";
import { COREIcon } from "../Content/COREIcon";

interface COREMenuProps {
  testID: TestID;
  className?: string;
  hideHorizontalBorder?: boolean;
  hideLeftRightBorder?: boolean;
  overflowedIndicator?: React.ReactElement;
  showAllHorizontalBorder?: boolean;
  // Styles the wrapper around the menu rather than the menu itself. Needed to
  // make the wrapper the scroll container, since `style` goes to the menu.
  containerStyle?: React.CSSProperties;
}

export const MenuModeContext = React.createContext<string | undefined>(
  undefined
);

export const COREMenu: React.FC<COREMenuProps & MenuProps> = ({
  testID,
  mode = "vertical",
  className,
  showAllHorizontalBorder,
  hideHorizontalBorder = false,
  hideLeftRightBorder,
  overflowedIndicator,
  containerStyle,
  triggerSubMenuAction,
  ...props
}) => {
  const hasCoarsePointer = useHasCoarsePointer();

  // A coarse pointer has no real hover, so a hover trigger only binds
  // mouseenter — after a tap the emulated hover stays on the title and the
  // popup can never be reopened. Fall back to click, but let callers override.
  const mergedTriggerSubMenuAction: MenuProps["triggerSubMenuAction"] =
    triggerSubMenuAction ?? (hasCoarsePointer ? "click" : "hover");

  const indicator = overflowedIndicator ?? (
    <COREIcon icon={icon({ name: "bars", style: "solid" })} />
  );
  const overflowedElement = cloneElement(indicator, {
    className: "overflow-icon",
  });

  return (
    <TestIDWrapper
      testID={testID}
      className={"core-menu-container"}
      style={containerStyle}
    >
      <MenuModeContext.Provider value={mode}>
        <Menu
          {...props}
          mode={mode}
          className={classNames(className, "core-menu", {
            "show-all-horizontal-border": showAllHorizontalBorder,
            "hide-menu-horizontal-border": hideHorizontalBorder,
            "hide-menu-left-right-border": hideLeftRightBorder,
          })}
          selectable={false}
          expandIcon={
            <COREIcon
              icon={icon({ name: "caret-right", style: "solid" })}
              className={"expand-icon"}
            />
          }
          overflowedIndicator={overflowedElement}
          triggerSubMenuAction={mergedTriggerSubMenuAction}
        />
      </MenuModeContext.Provider>
    </TestIDWrapper>
  );
};
